import { PartnerIntegrationModalProvider } from 'lib-integrations-partner-widgets';
import { MetaConversation } from 'lib-shared-legacy-types';
import { useEffect } from 'react';

import { FlatCardContext } from '../../hooks/useFlatCardContext/useFlatCardContext';
import { useSidebarCards } from '../../hooks/useSidebarCards/useSidebarCards';
import { useSidebarMode } from '../../hooks/useSidebarMode';

import styles from './flatSidebar.module.scss';

/** Counts describing how many cards are hidden, for the header summary. */
export type SidebarCardsSummaryData = {
  readonly totalCount: number;
  readonly hiddenCount: number;
  readonly conditionHiddenCount: number;
};

type FlatSidebarProps = {
  readonly metaConversation: MetaConversation;
  readonly isMinimized: boolean;
  readonly isCardOpen: (key: string) => boolean;
  readonly toggleCard: (key: string) => void;
  /** When `true`, hidden cards are revealed (dimmed) in default mode. */
  readonly forceShowHidden: boolean;
  /** Reports hidden/total counts so the header can render its summary. */
  readonly onSummaryChange: (summary: SidebarCardsSummaryData) => void;
};

export const FlatSidebar = ({
  metaConversation,
  isMinimized,
  isCardOpen,
  toggleCard,
  forceShowHidden,
  onSummaryChange,
}: FlatSidebarProps): JSX.Element => {
  const sidebarMode = useSidebarMode();
  const {
    orderedCards,
    visibleCards,
    isHidden,
    isUserHidden,
    isConditionHidden,
    toggleVisibility,
    reorder,
    dragType,
    totalCount,
    hiddenCount,
    conditionHiddenCount,
  } = useSidebarCards({
    metaConversation,
    isCardOpen,
    toggleCard,
  });

  useEffect(() => {
    onSummaryChange({ totalCount, hiddenCount, conditionHiddenCount });
  }, [totalCount, hiddenCount, conditionHiddenCount, onSummaryChange]);

  // Show hidden cards while customizing or when the agent force-shows them.
  const revealHidden = sidebarMode === 'customization' || forceShowHidden;
  const list = revealHidden ? orderedCards : visibleCards;

  return (
    <PartnerIntegrationModalProvider>
      <div className={styles.cardList}>
        {list.map((card, index) => {
          const hidden = isHidden(card.key);
          const rendered = card.render({
            index,
            isMinimized,
            type: dragType,
            onMove: reorder,
          });

          return (
            <FlatCardContext.Provider
              key={card.key}
              value={{
                cardKey: card.key,
                isHidden: hidden,
                isUserHidden: isUserHidden(card.key),
                isConditionHidden: isConditionHidden(card.key),
                toggleVisibility: () => toggleVisibility(card.key),
              }}
            >
              {revealHidden && hidden ? (
                <div className={styles.dimmed}>{rendered}</div>
              ) : (
                rendered
              )}
            </FlatCardContext.Provider>
          );
        })}
      </div>
    </PartnerIntegrationModalProvider>
  );
};
